// ============================================================
// v2 operations endpoints (injected into v2Api so they share its auth,
// 401 handling and cache): error groups, proof pack, warehouse exports,
// management keys. Shapes: ./opsTypes.
// ============================================================

import type { ProjectAnalyticsConfig } from '@yourownmetric/shared'

import { v2Api } from './v2Api'
import { reportQuery, unwrap, type V2Envelope, type V2RangeArgs } from './v2Mappers'
import type {
    ErrorGroup,
    ErrorGroupDetail,
    ErrorGroupList,
    ErrorGroupQuery,
    ErrorGroupStatus,
    ExportRun,
    ExportRunResult,
    ExportTestResult,
    ManagementKey,
    ManagementKeyCreated,
    ProjectKey,
    ProjectKeyCreated,
    ProjectKeyScope,
    ManagementScope,
    OrderPreset,
    OrdersUploadResult,
    PrivacyEvidenceReport,
    ArchivePage,
    PrivacyEventSample,
    PrivacySubjectRequest,
    PrivacySubjectType,
    ReconSource,
    ReconciliationReport,
    WarehouseExport,
} from './opsTypes'

const p = (projectId: string) => `/projects/${encodeURIComponent(projectId)}`
const enc = encodeURIComponent

/** `?st=` for list endpoints: `-` = the project's own rows, absent = everything. */
function stParam(subTenant: string | null | undefined): string | undefined {
    if (subTenant === undefined || subTenant === null) {
        return undefined
    }
    return subTenant === '' ? '-' : subTenant
}

export function errorGroupQuery(a: ErrorGroupQuery): string {
    const q = new URLSearchParams()
    const st = stParam(a.subTenant)
    if (st) {
        q.set('st', st)
    }
    if (a.status && a.status.length > 0) {
        q.set('status', a.status.join(','))
    }
    if (a.q) {
        q.set('q', a.q)
    }
    q.set('sort', a.sort ?? 'last_seen')
    q.set('limit', String(a.limit ?? 50))
    q.set('offset', String(a.offset ?? 0))
    return q.toString()
}

export type ExportInput = Record<string, unknown>

export const opsApi = v2Api.injectEndpoints({
    endpoints: builder => ({
        // ---------- error groups ----------
        listErrorGroups: builder.query<ErrorGroupList, ErrorGroupQuery>({
            query: a => `${p(a.projectId)}/errors?${errorGroupQuery(a)}`,
            transformResponse: (res: {
                data: ErrorGroup[]
                total: number
                limit: number
                offset: number
            }) => ({ groups: res.data, total: res.total, limit: res.limit, offset: res.offset }),
            providesTags: ['ErrorGroups'],
        }),
        getErrorGroup: builder.query<ErrorGroupDetail, { projectId: string; id: string }>({
            query: a => `${p(a.projectId)}/errors/${enc(a.id)}?samples=5`,
            transformResponse: (res: V2Envelope<ErrorGroupDetail>) => unwrap(res),
            providesTags: ['ErrorGroups'],
        }),
        triageErrorGroups: builder.mutation<
            unknown,
            {
                projectId: string
                ids: string[]
                status?: ErrorGroupStatus
                assignee?: string | null
            }
        >({
            // One id → the single-group route (audited per group); several → bulk.
            query: ({ projectId, ids, ...body }) =>
                ids.length === 1
                    ? {
                          url: `${p(projectId)}/errors/${enc(ids[0])}`,
                          method: 'PATCH',
                          body,
                      }
                    : { url: `${p(projectId)}/errors`, method: 'PATCH', body: { ids, ...body } },
            invalidatesTags: ['ErrorGroups'],
        }),

        // ---------- revenue reconciliation ----------
        getReconciliation: builder.query<
            ReconciliationReport,
            V2RangeArgs & { tolerance?: number; limit?: number; fresh?: boolean }
        >({
            // `fresh` (admins) skips the 60 s report cache, right after orders were uploaded.
            query: a =>
                `${p(a.projectId)}/reports/reconciliation?${reportQuery(a, {
                    tolerance: a.tolerance,
                    limit: a.limit ?? 100,
                    fresh: a.fresh ? 1 : undefined,
                })}`,
            transformResponse: (res: V2Envelope<ReconciliationReport>) => unwrap(res),
            providesTags: ['Reconciliation'],
        }),
        downloadReconciliationCsv: builder.mutation<string, V2RangeArgs & { tolerance?: number }>({
            query: a => ({
                url: `${p(a.projectId)}/reports/reconciliation?${reportQuery(a, {
                    tolerance: a.tolerance,
                    format: 'csv',
                })}`,
                responseHandler: 'text',
            }),
        }),
        uploadOrdersCsv: builder.mutation<
            OrdersUploadResult,
            { projectId: string; csv: string; preset: OrderPreset; subTenant?: string | null }
        >({
            query: a => {
                const q = new URLSearchParams({ preset: a.preset })
                const st = stParam(a.subTenant)
                if (st && st !== '-') {
                    q.set('st', st)
                }
                return {
                    url: `${p(a.projectId)}/reconciliation/orders/csv?${q.toString()}`,
                    method: 'POST',
                    body: a.csv,
                    headers: { 'content-type': 'text/csv' },
                }
            },
            transformResponse: (res: V2Envelope<OrdersUploadResult>) => unwrap(res),
            invalidatesTags: ['Reconciliation'],
        }),
        listReconSources: builder.query<ReconSource[], string>({
            query: projectId => `${p(projectId)}/reconciliation/sources`,
            transformResponse: (res: V2Envelope<ReconSource[]>) => unwrap(res),
            providesTags: ['ReconSources'],
        }),
        createReconSource: builder.mutation<
            ReconSource,
            {
                projectId: string
                name: string
                preset: OrderPreset
                secret?: string
                sub_tenant?: string | null
            }
        >({
            query: ({ projectId, ...body }) => ({
                url: `${p(projectId)}/reconciliation/sources`,
                method: 'POST',
                body,
            }),
            transformResponse: (res: V2Envelope<ReconSource>) => unwrap(res),
            invalidatesTags: ['ReconSources'],
        }),
        deleteReconSource: builder.mutation<unknown, { projectId: string; id: string }>({
            query: a => ({
                url: `${p(a.projectId)}/reconciliation/sources/${enc(a.id)}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['ReconSources'],
        }),

        // ---------- privacy evidence ----------
        getPrivacyEvidence: builder.query<PrivacyEvidenceReport, V2RangeArgs>({
            query: a => `${p(a.projectId)}/reports/privacy-evidence?${reportQuery(a)}`,
            transformResponse: (res: V2Envelope<PrivacyEvidenceReport>) => unwrap(res),
        }),
        downloadPrivacyEvidenceHtml: builder.mutation<string, V2RangeArgs>({
            query: a => ({
                url: `${p(a.projectId)}/reports/privacy-evidence?${reportQuery(a, { format: 'html' })}`,
                responseHandler: 'text',
            }),
        }),

        getPrivacyEventSample: builder.query<PrivacyEventSample, V2RangeArgs>({
            query: a =>
                `${p(a.projectId)}/reports/privacy-evidence/events?${reportQuery(a, { limit: 50 })}`,
            transformResponse: (res: V2Envelope<PrivacyEventSample>) => unwrap(res),
        }),

        // ---------- data-subject requests (DPA §9) ----------
        listPrivacyRequests: builder.query<PrivacySubjectRequest[], string>({
            query: projectId => `${p(projectId)}/privacy/requests`,
            transformResponse: (res: V2Envelope<PrivacySubjectRequest[]>) => unwrap(res),
            providesTags: ['PrivacyRequests'],
        }),
        /** Opens (or returns the open / downloadable) access request: status only. */
        requestSubjectAccess: builder.mutation<
            PrivacySubjectRequest,
            { projectId: string; type: PrivacySubjectType; subject: string }
        >({
            query: a => ({
                url: `${p(a.projectId)}/privacy/subjects/${enc(a.subject)}?type=${a.type}&format=status`,
            }),
            transformResponse: (res: V2Envelope<PrivacySubjectRequest>) => unwrap(res),
            invalidatesTags: ['PrivacyRequests'],
        }),
        requestSubjectErasure: builder.mutation<
            PrivacySubjectRequest,
            { projectId: string; type: PrivacySubjectType; subject: string }
        >({
            query: a => ({
                url: `${p(a.projectId)}/privacy/subjects/${enc(a.subject)}?type=${a.type}`,
                method: 'DELETE',
            }),
            transformResponse: (res: V2Envelope<PrivacySubjectRequest>) => unwrap(res),
            invalidatesTags: ['PrivacyRequests'],
        }),
        downloadSubjectExport: builder.mutation<string, { projectId: string; id: string }>({
            query: a => ({
                url: `${p(a.projectId)}/privacy/requests/${enc(a.id)}/export`,
                responseHandler: 'text',
            }),
        }),

        // ---------- raw archive export (any age) ----------
        downloadArchivePage: builder.mutation<
            ArchivePage,
            { projectId: string; from: string; to: string; cursor?: string | null }
        >({
            query: a => {
                const q = new URLSearchParams({ from: a.from, to: a.to })
                if (a.cursor) {
                    q.set('cursor', a.cursor)
                }
                return {
                    url: `${p(a.projectId)}/archive/export?${q}`,
                    responseHandler: async (res: Response): Promise<ArchivePage | unknown> =>
                        res.ok
                            ? {
                                  text: await res.text(),
                                  files: Number(res.headers.get('X-YOM-Files') ?? 0),
                                  next: res.headers.get('X-YOM-Next-Cursor'),
                              }
                            : res.json(),
                }
            },
        }),

        // ---------- warehouse exports ----------
        listExports: builder.query<WarehouseExport[], string>({
            query: projectId => `${p(projectId)}/exports`,
            transformResponse: (res: V2Envelope<WarehouseExport[]>) => unwrap(res),
            providesTags: ['Exports'],
        }),
        createExport: builder.mutation<WarehouseExport, { projectId: string; body: ExportInput }>({
            query: a => ({ url: `${p(a.projectId)}/exports`, method: 'POST', body: a.body }),
            transformResponse: (res: V2Envelope<WarehouseExport>) => unwrap(res),
            invalidatesTags: ['Exports'],
        }),
        updateExport: builder.mutation<
            WarehouseExport,
            { projectId: string; id: string; body: ExportInput }
        >({
            query: a => ({
                url: `${p(a.projectId)}/exports/${enc(a.id)}`,
                method: 'PATCH',
                body: a.body,
            }),
            transformResponse: (res: V2Envelope<WarehouseExport>) => unwrap(res),
            invalidatesTags: ['Exports'],
        }),
        deleteExport: builder.mutation<unknown, { projectId: string; id: string }>({
            query: a => ({ url: `${p(a.projectId)}/exports/${enc(a.id)}`, method: 'DELETE' }),
            invalidatesTags: ['Exports'],
        }),
        validateExport: builder.mutation<
            ExportTestResult,
            { projectId: string; body: ExportInput }
        >({
            query: a => ({
                url: `${p(a.projectId)}/exports/validate`,
                method: 'POST',
                body: a.body,
            }),
            transformResponse: (res: V2Envelope<ExportTestResult>) => unwrap(res),
        }),
        testExport: builder.mutation<ExportTestResult, { projectId: string; id: string }>({
            query: a => ({ url: `${p(a.projectId)}/exports/${enc(a.id)}/test`, method: 'POST' }),
            transformResponse: (res: V2Envelope<ExportTestResult>) => unwrap(res),
        }),
        runExport: builder.mutation<ExportRunResult, { projectId: string; id: string }>({
            query: a => ({ url: `${p(a.projectId)}/exports/${enc(a.id)}/run`, method: 'POST' }),
            transformResponse: (res: V2Envelope<ExportRunResult>) => unwrap(res),
            invalidatesTags: ['Exports', 'ExportRuns'],
        }),
        listExportRuns: builder.query<ExportRun[], { projectId: string; id: string }>({
            query: a => `${p(a.projectId)}/exports/${enc(a.id)}/runs?limit=30`,
            transformResponse: (res: V2Envelope<ExportRun[]>) => unwrap(res),
            providesTags: ['ExportRuns'],
        }),

        // ---------- project config ----------
        // PUT replaces the whole config, so callers send the current config with their change.
        updateProjectConfig: builder.mutation<
            unknown,
            { projectId: string; config: ProjectAnalyticsConfig }
        >({
            query: a => ({ url: `${p(a.projectId)}/config`, method: 'PUT', body: a.config }),
            invalidatesTags: ['V2Config'],
        }),

        // ---------- management keys (organization owners) ----------
        listManagementKeys: builder.query<ManagementKey[], void>({
            query: () => '/management-keys',
            transformResponse: (res: V2Envelope<ManagementKey[]>) => unwrap(res),
            providesTags: ['ManagementKeys'],
        }),
        createManagementKey: builder.mutation<
            ManagementKeyCreated,
            { name: string; scopes: ManagementScope[]; expires_at: string | null }
        >({
            query: body => ({ url: '/management-keys', method: 'POST', body }),
            transformResponse: (res: V2Envelope<ManagementKeyCreated>) => unwrap(res),
            invalidatesTags: ['ManagementKeys'],
        }),
        revokeManagementKey: builder.mutation<unknown, string>({
            query: id => ({ url: `/management-keys/${enc(id)}`, method: 'DELETE' }),
            invalidatesTags: ['ManagementKeys'],
        }),
        // ---------- project keys (pk / sk, sub-tenant bound) ----------
        listProjectKeys: builder.query<ProjectKey[], string>({
            query: projectId => `/projects/${enc(projectId)}/keys`,
            transformResponse: (res: V2Envelope<ProjectKey[]>) => unwrap(res),
            providesTags: ['ProjectKeys'],
        }),
        createProjectKey: builder.mutation<
            ProjectKeyCreated,
            {
                projectId: string
                name: string
                scopes: ProjectKeyScope[]
                sub_tenant: string | null
                expires_at: string | null
            }
        >({
            query: ({ projectId, ...body }) => ({
                url: `/projects/${enc(projectId)}/keys`,
                method: 'POST',
                body,
            }),
            transformResponse: (res: V2Envelope<ProjectKeyCreated>) => unwrap(res),
            invalidatesTags: ['ProjectKeys'],
        }),
        revokeProjectKey: builder.mutation<unknown, { projectId: string; id: string }>({
            query: a => ({
                url: `/projects/${enc(a.projectId)}/keys/${enc(a.id)}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['ProjectKeys'],
        }),
    }),
})

export const {
    useListErrorGroupsQuery,
    useGetErrorGroupQuery,
    useTriageErrorGroupsMutation,
    useGetReconciliationQuery,
    useDownloadReconciliationCsvMutation,
    useUploadOrdersCsvMutation,
    useListReconSourcesQuery,
    useCreateReconSourceMutation,
    useDeleteReconSourceMutation,
    useGetPrivacyEvidenceQuery,
    useDownloadPrivacyEvidenceHtmlMutation,
    useGetPrivacyEventSampleQuery,
    useListPrivacyRequestsQuery,
    useRequestSubjectAccessMutation,
    useRequestSubjectErasureMutation,
    useDownloadSubjectExportMutation,
    useDownloadArchivePageMutation,
    useListExportsQuery,
    useCreateExportMutation,
    useUpdateExportMutation,
    useDeleteExportMutation,
    useValidateExportMutation,
    useTestExportMutation,
    useRunExportMutation,
    useListExportRunsQuery,
    useUpdateProjectConfigMutation,
    useListManagementKeysQuery,
    useCreateManagementKeyMutation,
    useRevokeManagementKeyMutation,
    useListProjectKeysQuery,
    useCreateProjectKeyMutation,
    useRevokeProjectKeyMutation,
} = opsApi
